import { useState, useEffect, useRef, useCallback } from 'react';
import { apiService } from '../services/api';
import {
  MAX_DURATION_MS,
  TIMESLICE_MS,
  defaultRecordingName,
} from '../models/VoiceRecordingModel';
import {
  appendChunk,
  saveDraftMeta,
  deleteDraft,
  getLatestDraft,
} from '../models/voiceDraftStore';

// Preferred capture formats, best-first. Chrome/Edge favour webm/opus; Safari only
// supports mp4/aac. MediaRecorder.isTypeSupported drives the pick (codec detection).
const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
];

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return 'audio/webm';
  }
  const match = MIME_CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t));
  return match || '';
}

function extForMime(mime) {
  if (!mime) return 'webm';
  if (mime.includes('mp4') || mime.includes('m4a') || mime.includes('aac')) return 'm4a';
  if (mime.includes('ogg')) return 'ogg';
  return 'webm';
}

function safeFileName(name) {
  return (name || 'voice-note').replace(/[^\w.-]+/g, '_').slice(0, 80) || 'voice-note';
}

function newDraftId() {
  return `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * ViewModel for in-chat voice recording (PRD-941). Wraps getUserMedia / MediaRecorder /
 * AudioContext imperatively and drives the VoiceRecordingModel. Returns stable callbacks.
 */
export const useVoiceRecordingViewModel = (model) => {
  const [state, setState] = useState(model.getState());

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const seqRef = useRef(0);
  const mimeRef = useRef('');
  const recordedAtRef = useRef(null);

  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const tickRef = useRef(null);
  const lastTickRef = useRef(0);
  const elapsedRef = useRef(0);

  useEffect(() => {
    const unsubscribe = model.subscribe(setState);
    setState(model.getState());
    return unsubscribe;
  }, [model]);

  const stopTimers = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const teardownAudio = useCallback(() => {
    stopTimers();
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    } catch (e) { /* ignore */ }
    streamRef.current = null;
    try {
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close();
      }
    } catch (e) { /* ignore */ }
    audioCtxRef.current = null;
    analyserRef.current = null;
  }, [stopTimers]);

  // Cleanup on unmount.
  useEffect(() => () => teardownAudio(), [teardownAudio]);

  // Sample input level (RMS) from the analyser for the meter.
  const sampleLevel = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return 0;
    const buf = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(buf);
    let sumSq = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128;
      sumSq += v * v;
    }
    const rms = Math.sqrt(sumSq / buf.length);
    return Math.min(1, rms * 2.5); // light gain so normal speech reads mid-meter
  }, []);

  const finalize = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try { recorder.stop(); } catch (e) { /* onstop still fires teardown */ }
    } else {
      // No active recorder (e.g. already stopped) — assemble what we have.
      const mime = mimeRef.current || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: mime });
      model.setRecorded(blob, mime);
      if (!model.getState().name) model.setName(defaultRecordingName());
      model.setPhase('review');
      teardownAudio();
    }
  }, [model, teardownAudio]);

  const startTick = useCallback(() => {
    lastTickRef.current = Date.now();
    tickRef.current = setInterval(() => {
      const now = Date.now();
      elapsedRef.current += now - lastTickRef.current;
      lastTickRef.current = now;
      model.setElapsed(elapsedRef.current);
      model.setLevel(sampleLevel());
      // Hard cap: auto-stop + auto-finalize at 10:00, keeping the recording.
      if (elapsedRef.current >= MAX_DURATION_MS) {
        stopTimers();
        model.setPhase('finalizing');
        finalize();
      }
    }, 200);
  }, [model, sampleLevel, stopTimers, finalize]);

  const start = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      model.setError('Microphone capture is not supported in this browser.');
      return;
    }
    model.setPhase('requesting');
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      // NotAllowedError / SecurityError → permission denied; anything else is a real error.
      if (err && (err.name === 'NotAllowedError' || err.name === 'SecurityError')) {
        model.setPhase('denied');
      } else {
        model.setError(err?.message || 'Could not access the microphone.');
      }
      return;
    }

    streamRef.current = stream;
    chunksRef.current = [];
    seqRef.current = 0;
    elapsedRef.current = 0;
    recordedAtRef.current = new Date().toISOString();

    const draftId = newDraftId();
    model.setDraftId(draftId);

    const mime = pickMimeType();
    mimeRef.current = mime;
    let recorder;
    try {
      recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch (e) {
      recorder = new MediaRecorder(stream);
    }
    mimeRef.current = recorder.mimeType || mime || 'audio/webm';
    recorderRef.current = recorder;

    saveDraftMeta(draftId, {
      name: defaultRecordingName(),
      mimeType: mimeRef.current,
      recordedAt: recordedAtRef.current,
      durationSeconds: 0,
    });

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunksRef.current.push(e.data);
        const seq = seqRef.current++;
        // Persist each chunk so a crash leaves a recoverable draft.
        appendChunk(draftId, seq, e.data);
        saveDraftMeta(draftId, {
          name: model.getState().name || defaultRecordingName(),
          mimeType: mimeRef.current,
          recordedAt: recordedAtRef.current,
          durationSeconds: Math.round(elapsedRef.current / 1000),
        });
      }
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeRef.current });
      model.setRecorded(blob, mimeRef.current);
      if (!model.getState().name) model.setName(defaultRecordingName());
      model.setPhase('review');
      teardownAudio();
    };

    // Level meter via AudioContext analyser.
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        const sourceNode = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        sourceNode.connect(analyser);
        audioCtxRef.current = ctx;
        analyserRef.current = analyser;
      }
    } catch (e) {
      // Meter is non-essential; recording proceeds without it.
      console.warn('[VoiceRecordingVM] level meter unavailable:', e);
    }

    recorder.start(TIMESLICE_MS);
    model.setPhase('recording');
    startTick();
  }, [model, startTick, teardownAudio]);

  const pause = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state === 'recording') {
      try { recorder.pause(); } catch (e) { /* ignore */ }
    }
    stopTimers();
    model.setLevel(0);
    model.setPhase('paused');
  }, [model, stopTimers]);

  const resume = useCallback(() => {
    const recorder = recorderRef.current;
    // Only resume a genuinely paused, live recorder. A recovered draft has no live
    // recorder — entering "recording" then would tick with no audio captured.
    if (!recorder || recorder.state !== 'paused') return;
    try { recorder.resume(); } catch (e) { /* ignore */ }
    model.setPhase('recording');
    startTick();
  }, [model, startTick]);

  const stop = useCallback(() => {
    stopTimers();
    model.setPhase('finalizing');
    finalize();
  }, [model, stopTimers, finalize]);

  // "Save" from the paused state: move to the naming/submit step WITHOUT stopping the
  // recorder, so the user can hit Back and Resume to continue the same take. The blob is
  // assembled lazily on submit (see finalizeToBlob). Keeps the mic stream alive meanwhile.
  const save = useCallback(() => {
    stopTimers();
    const recorder = recorderRef.current;
    if (recorder && recorder.state === 'recording') {
      try { recorder.pause(); } catch (e) { /* ignore */ }
    }
    model.setLevel(0);
    if (!model.getState().name) model.setName(defaultRecordingName());
    model.setPhase('review');
  }, [model, stopTimers]);

  // "Back" from the naming step: return to the paused controls to continue recording.
  const back = useCallback(() => {
    model.setPhase('paused');
  }, [model]);

  // Stop the (possibly still-paused) recorder and resolve the assembled audio blob.
  const finalizeToBlob = useCallback(() => {
    return new Promise((resolve) => {
      const recorder = recorderRef.current;
      const mime = mimeRef.current || 'audio/webm';
      if (!recorder || recorder.state === 'inactive') {
        resolve(new Blob(chunksRef.current, { type: mime }));
        return;
      }
      recorder.onstop = () => {
        teardownAudio();
        resolve(new Blob(chunksRef.current, { type: mime }));
      };
      try {
        recorder.stop();
      } catch (e) {
        teardownAudio();
        resolve(new Blob(chunksRef.current, { type: mime }));
      }
    });
  }, [teardownAudio]);

  const cancel = useCallback(async () => {
    stopTimers();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      // Detach BOTH handlers before stopping: stop() flushes a final `dataavailable`,
      // and if its handler still ran it would re-persist a chunk AFTER we delete the
      // draft below — resurrecting it so the next session "finds a draft". Drop it.
      recorder.ondataavailable = null;
      recorder.onstop = null;
      try { recorder.stop(); } catch (e) { /* ignore */ }
    }
    chunksRef.current = [];
    teardownAudio();
    const { draftId } = model.getState();
    if (draftId) await deleteDraft(draftId);
    model.reset();
  }, [model, stopTimers, teardownAudio]);

  const setName = useCallback((name) => {
    model.setName(name);
  }, [model]);

  // Submit: create the dataset, upload the recording through the chat_recording path,
  // then kick indexing. Returns the created dataset id, or null on failure.
  const submit = useCallback(async () => {
    const s = model.getState();
    // If saved-but-not-finalized (recorder kept paused so Back/Resume could continue),
    // stop it now and assemble the blob before uploading.
    let blob = s.recordedBlob;
    let mime = s.mimeType || mimeRef.current || 'audio/webm';
    if (!blob) {
      blob = await finalizeToBlob();
      mime = mimeRef.current || 'audio/webm';
      model.setRecorded(blob, mime);
    }
    if (!blob || blob.size === 0) {
      model.setError('No recording to submit.');
      return null;
    }
    const name = (s.name || '').trim() || defaultRecordingName();
    model.setPhase('submitting');
    model.setError(null);
    try {
      const ext = extForMime(mime);
      const file = new File([blob], `${safeFileName(name)}.${ext}`, { type: mime });
      const created = await apiService.createDataset({ name, description: name, prompt: '' });
      if (!created?.id) throw new Error('Dataset creation returned no id');
      await apiService.uploadFiles(created.id, [file], 'chat_recording', '', '', {
        duration_seconds: s.durationSeconds,
        recorded_at: recordedAtRef.current || new Date().toISOString(),
        mime_type: mime,
      });
      await apiService.extractFields(created.id);
      if (s.draftId) await deleteDraft(s.draftId);
      model.setPhase('submitted');
      return created.id;
    } catch (error) {
      console.error('[VoiceRecordingVM] submit failed:', error);
      const detail = error?.responseData?.detail || error?.message || 'Failed to submit recording.';
      model.setError(typeof detail === 'string' ? detail : 'Failed to submit recording.');
      model.setPhase('review'); // keep the recording so the user can retry
      return null;
    }
  }, [model, finalizeToBlob]);

  // Draft recovery: surface the most-recent recoverable recording on return.
  const restoreDraft = useCallback(async () => {
    const draft = await getLatestDraft();
    if (!draft || !draft.blob || draft.blob.size === 0) return false;
    recordedAtRef.current = draft.recordedAt || new Date().toISOString();
    mimeRef.current = draft.mimeType;
    model.loadDraft(draft);
    return true;
  }, [model]);

  const discardDraft = useCallback(async () => {
    const { draftId } = model.getState();
    if (draftId) await deleteDraft(draftId);
    model.reset();
  }, [model]);

  const reset = useCallback(() => {
    teardownAudio();
    model.reset();
  }, [model, teardownAudio]);

  return {
    ...state,
    start,
    pause,
    resume,
    stop,
    save,
    back,
    cancel,
    setName,
    submit,
    restoreDraft,
    discardDraft,
    reset,
    MAX_DURATION_MS,
  };
};
